import {
  Card,
  Col,
  type FormInstance,
  Radio,
  Row,
  Spin,
  Tree as TreeAntd,
} from 'antd';
import { useContext, useState, useRef, useEffect } from 'react';
import Tree from 'react-d3-tree';
import {
  ApartmentOutlined,
  CarOutlined,
  EnvironmentOutlined,
  NumberOutlined,
  RightOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ButtonLink from 'components/theme_components/ButtonLink';
import HeroTitle from 'components/theme_components/HeroTitle';
import { useQuery } from 'react-query';
import {
  type companyHierarchyTree,
  getCompanyHierarchyTreeById,
} from 'services/thirdParty';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'components/Auth/Auth';

export type FlowProps = {
  tpid: number;
  form: FormInstance;
  color: string;
  itemsCompanyHierarchyTree: any[];
  setActiveTab?: (tab: string) => void;
};

const Flow = ({
  form,
  color,
  tpid,
  itemsCompanyHierarchyTree,
  setActiveTab,
}: FlowProps) => {
  const ensureArray = (data: any): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  };

  const convertToTreeData = (nodes: companyHierarchyTree[] | any): any[] => {
    const nodesArray = ensureArray(nodes);

    return nodesArray.map((node: any) => ({
      tpname: node.tpname,
      tpid: node.tpid,
      tptype: node.tptype,
      typelabel: node.typelabel,
      tpBrand: node.tpBrand,
      tpReference: node.tpReference,
      tpAdress: node.tpAdress,
      city: node.city,
      legalId: node.legalId,
      tpparentname: node.tpparentname,
      children: node.children,
      originalData: node,
    }));
  };

  const convertToTreeDataHorizontal = (
    nodes: companyHierarchyTree[] | any
  ): any[] => {
    const nodesArray = ensureArray(nodes);
    console.log('convertToTreeDataHorizontal', { nodesArray, nodes });

    return nodesArray.map((node: any) => ({
      tpname: node.tpname,
      tpid: node.tpid,
      tptype: node.tptype,
      typelabel: node.typelabel,
      tpBrand: node.tpBrand,
      tpReference: node.tpReference,
      tpAdress: node.tpAdress,
      city: node.city,
      legalId: node.legalId,
      tpparentname: node.tpparentname,
      children: convertToTreeDataHorizontal(node.childs),
      originalData: node,
    }));
  };

  const convertToD3TreeData = (nodes: companyHierarchyTree[] | any): any => {
    const nodesArray = ensureArray(nodes);

    return nodesArray.map((node: companyHierarchyTree) => ({
      tpname: node?.tpname,
      tpid: node?.tpid,
      tptype: node?.tptype,
      typelabel: node?.typelabel,
      tpBrand: node?.tpBrand,
      tpReference: node?.tpReference,
      tpAdress: node.tpAdress,
      city: node.city,
      tpFiscalId: node?.tpFiscalId,
      tpParentCompanyName: node?.tpParentCompanyName,
      children: node.childs || [],
      rawChildren: node?.childs,
      originalData: node,
    }));
  };

  // Fonction pour calculer la largeur nécessaire basée sur le nombre d'enfants
  const calculateTreeWidth = (treeData: any): number => {
    const countMaxSiblings = (node: any, level = 0): number => {
      if (!node) return 0;

      let maxAtThisLevel = 0;
      let maxAtChildLevels = 0;

      if (node.children && Array.isArray(node.children)) {
        maxAtThisLevel = node.children.length;

        // Récursivement vérifier les niveaux enfants
        for (const child of node.children) {
          maxAtChildLevels = Math.max(
            maxAtChildLevels,
            countMaxSiblings(child, level + 1)
          );
        }
      }

      return Math.max(maxAtThisLevel, maxAtChildLevels);
    };

    const maxSiblings = countMaxSiblings(treeData);

    // Largeur de base + largeur supplémentaire pour chaque enfant au-delà de 4
    const baseWidth = 1700; // Largeur de base
    const nodeWidth = 400; // Largeur approximative d'un nœud

    if (maxSiblings > 4) {
      return baseWidth + (maxSiblings - 4) * nodeWidth;
    }

    return baseWidth;
  };

  const CustomNode = ({
    nodeDatum,
    orientation,
    onNodeClick,
  }: {
    nodeDatum: any;
    orientation: string;
    onNodeClick: (node: any) => void;
  }) => {
    const handleClick = () => {
      onNodeClick(nodeDatum);
    };

    return orientation === 'vertical' ? (
      <foreignObject
        width={
          nodeDatum.__rd3t.depth === 0
            ? '1000px'
            : nodeDatum.__rd3t.depth === 1
            ? '380px'
            : '200px'
        }
        height={'100%'}
        x={
          nodeDatum.__rd3t.depth === 0
            ? '-500'
            : nodeDatum.tptype === 'CONC'
            ? '-130'
            : nodeDatum.__rd3t.depth === 1
            ? '-150'
            : '-84'
        }
        y={
          nodeDatum.__rd3t.depth === 0 || nodeDatum.__rd3t.depth === 1
            ? '-60'
            : '40'
        }
        className={nodeDatum.tptype}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        {nodeDatum.__rd3t.depth === 0 ? (
          <>
            {console.log(nodeDatum, tpid)}
            <div className="hide"></div>
            <div
              className={
                tpid == nodeDatum.tpid
                  ? 'blue-002c6e SPMCard orangeCard'
                  : 'blue-002c6e SPMCard'
              }
            >
              <span className="bold font-size-20">{nodeDatum.tpname}</span>
              <br />
              <Row align="middle" className="gray-6B7181">
                <Col span={1}>
                  <EnvironmentOutlined className="float-right font-size-18" />
                </Col>
                <Col span={7} className="text-left">
                  <span className="font-size-16">{nodeDatum.tpAdress}</span>
                </Col>
                <Col span={8}>
                  <NumberOutlined className="font-size-18" />{' '}
                  <span className="font-size-16">{nodeDatum.tpReference}</span>
                </Col>
                <Col span={8}>
                  <CarOutlined className="font-size-18" />{' '}
                  <span className="font-size-16">{nodeDatum.tpBrand}</span>
                </Col>
              </Row>
            </div>
          </>
        ) : nodeDatum.__rd3t.depth === 1 && nodeDatum.tptype == 'CONC' ? (
          <div
            className={
              tpid == nodeDatum.tpid
                ? 'blue-002c6e CONCCard orangeCard'
                : 'blue-002c6e CONCCard'
            }
            onClick={(e) => {
              e.stopPropagation();
              onNodeClick(nodeDatum);
            }}
          >
            <span className="bold font-size-20">{nodeDatum.tpname}</span>
            <br />
            <span className="gray-6B7181">
              <NumberOutlined className="font-size-18" />{' '}
              {nodeDatum.tpReference}- <CarOutlined className="font-size-18" />{' '}
              {nodeDatum.tpBrand}
            </span>
          </div>
        ) : (
          nodeDatum.__rd3t.depth === 1 && (
            <div
              className={
                tpid == nodeDatum.tpid
                  ? 'blue-002c6e PMCard orangeCard'
                  : 'blue-002c6e PMCard'
              }
            >
              <span className="bold font-size-20">{nodeDatum.tpname}</span>
              <br />
              <span className="gray-6B7181">
                <NumberOutlined className="font-size-18" />
                <span className="font-size-16">{nodeDatum.tpReference}</span>
              </span>
              <br />
              <div className="gray-6B7181 text-left">
                <CarOutlined className="font-size-18" />{' '}
                <span className="font-size-16">{nodeDatum.tpBrand}</span>
              </div>
              <div className="fil"></div>
              <TreeAntd
                className="conc-Tree"
                treeData={convertToTreeData(nodeDatum.childs)}
                showLine
                titleRender={(node: any) => {
                  return (
                    <div
                      className={
                        tpid == node.tpid
                          ? 'blue-002c6e CONCCard orangeCard'
                          : 'blue-002c6e CONCCard'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onNodeClick(node);
                      }}
                    >
                      <span className="bold font-size-20">{node.tpname}</span>
                      <br />
                      <span className="gray-6B7181">
                        <NumberOutlined className="font-size-18" />{' '}
                        {node.tpReference}-{console.log(tpid, nodeDatum.tpid)}
                        <CarOutlined className="font-size-18" /> {node.tpBrand}
                      </span>
                    </div>
                  );
                }}
              />
            </div>
          )
        )}
      </foreignObject>
    ) : (
      <foreignObject
        className={nodeDatum.tptype}
        width="500px"
        height="200px"
        x="-50"
        y={'-30'}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <div
          className={
            tpid == nodeDatum.tpid ? 'nodename orangeName' : 'nodename'
          }
        >
          {nodeDatum.tptype} {nodeDatum.tpname}
        </div>
        <div
          className={
            tpid == nodeDatum.tpid ? 'nodetree nodetreeZoom' : 'nodetree'
          }
        >
          {nodeDatum.tptype}
        </div>
      </foreignObject>
    );
  };

  const RecursiveTree = ({
    treeData,
    onNodeClick,
  }: {
    treeData: companyHierarchyTree;
    onNodeClick: (node: any) => void;
  }) => {
    const treeContainerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({
      width: 1700,
      height: 600,
    });
    const [isInitialScrollSet, setIsInitialScrollSet] = useState(false);

    const formattedTreeData = convertToTreeData(treeData);
    const calculatedWidth = calculateTreeWidth(formattedTreeData[0]);
    const needsScroll = calculatedWidth > 1700;

    useEffect(() => {
      if (treeContainerRef.current) {
        const updateDimensions = () => {
          const rect = treeContainerRef.current!.getBoundingClientRect();
          setContainerDimensions({
            width: Math.max(calculatedWidth, rect.width),
            height: 600,
          });
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
      }
    }, [calculatedWidth]);

    // Nouveau useEffect pour centrer le scroll initialement
    useEffect(() => {
      if (treeContainerRef.current && needsScroll && !isInitialScrollSet) {
        const container = treeContainerRef.current;
        const containerWidth = container.clientWidth;
        const contentWidth = calculatedWidth;

        // Calculer la position pour centrer le contenu
        const scrollPosition = Math.max(0, (contentWidth - containerWidth) / 2);

        // Petit délai pour s'assurer que le DOM est rendu
        setTimeout(() => {
          container.scrollLeft = scrollPosition;
          setIsInitialScrollSet(true);
        }, 100);
      }
    }, [calculatedWidth, needsScroll, isInitialScrollSet]);

    // Réinitialiser le flag quand les données changent
    useEffect(() => {
      setIsInitialScrollSet(false);
    }, [treeData]);

    // S'assurer que treeData est un objet valide avant de le traiter
    if (!treeData) return null;

    return (
      <div
        ref={treeContainerRef}
        style={{
          width: '100%',
          height: '600px',
          overflowX: needsScroll ? 'auto' : 'hidden',
          overflowY: 'hidden',
          border: '1px solid #f0f0f0',
          borderRadius: '6px',
          position: 'relative',
          scrollBehavior: 'smooth', // Ajout d'un scroll fluide
        }}
        className="recursive-tree-container"
      >
        <div
          style={{
            width: `${containerDimensions.width}px`,
            height: '600px',
            minWidth: '1700px',
          }}
        >
          <Tree
            data={formattedTreeData}
            orientation={'vertical'}
            translate={{ x: containerDimensions.width / 2, y: 100 }}
            nodeSize={{ x: 400, y: 150 }}
            pathFunc="step"
            separation={{ siblings: 1, nonSiblings: 1 }}
            renderCustomNodeElement={(rd3tProps) => (
              <CustomNode
                {...rd3tProps}
                orientation="vertical"
                onNodeClick={onNodeClick}
              />
            )}
            svgClassName={treeData?.tptype}
            draggable={false}
            zoomable={false}
            key={treeData?.tptype}
          />
        </div>
      </div>
    );
  };

  const navigate = useNavigate();
  const { isInspect } = useContext(AuthContext);

  const NodeDetailsCard = ({ node }: { node: companyHierarchyTree | null }) => {
    if (!node) return null;

    return (
      <Card title={null} bordered={false} className="TreeCard">
        <div className="card-content">
          <div className="mb-3">
            <GetLabel label="info" className="font-size-25 color-blue " />
          </div>
          <div className="mb-2">
            <GetLabel label="name" className="font-size-15  gray-color" />
            <p>
              {' '}
              {node.typelabel} {node.tpname}
            </p>
          </div>
          {node.tptype !== 'SPM' && (
            <div className="mb-2">
              <GetLabel
                label={`name${node.tptype}`}
                className="font-size-15  gray-color"
              />
              <p> {node.tpParentCompanyName}</p>
            </div>
          )}
          {node.tptype !== 'SPM' && node.tptype !== 'CONC' && (
            <div className="mb-2">
              <GetLabel label="legalId" className="font-size-15  gray-color" />
              <p> {node.tpFiscalId}</p>
            </div>
          )}
          {node.tptype === 'CONC' && (
            <div className="mb-2">
              <GetLabel
                label="tpReference"
                className="font-size-15  gray-color"
              />
              <p> {node.tpReference}</p>
            </div>
          )}

          {node.city && (
            <div className="mb-2">
              <GetLabel label="address" className="font-size-15  gray-color" />
              <p> {node.city}</p>
            </div>
          )}

          {node.tpBrand && (
            <div className="mb-2">
              <GetLabel
                label={`brand${node.tptype}`}
                className="font-size-15  gray-color"
              />
              <p> {node.tpBrand}</p>
            </div>
          )}
        </div>
        <Row justify={'center'}>
          <ButtonLink
            type="primary"
            comcode="seethirdparty"
            htmlType="submit"
            className="ut-submit-btn"
            icon={<RightOutlined />}
            onClick={() => {
              if (!isInspect) {
                setActiveTab && setActiveTab('1');
                navigate(`/thirdparty-sfs/${node.tpid}/${node.tptype}`);
              }
            }}
          />
        </Row>
      </Card>
    );
  };

  const { data: Newdata, refetch } = useQuery(
    ['getCompanyHierarchyTreeById', { tpid: Number(tpid) }],
    () =>
      getCompanyHierarchyTreeById({ tpid: Number(tpid) }).then(
        (res) => res.data || []
      )
  );
  const [orientationH, setorientationH] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<companyHierarchyTree | null>(
    null
  );

  // Utiliser Newdata au lieu de data, avec vérification de type
  const treeData = Newdata ? convertToD3TreeData(ensureArray(Newdata)) : [];
  const treeDatah = Newdata ? convertToTreeData(ensureArray(Newdata)) : [];

  const handleNodeClick = (node: any) => {
    const nodeData = node.originalData || node;
    setSelectedNode({
      tpid: nodeData.tpid,
      tpname: nodeData.tpname || nodeData.title,
      tptype: nodeData.tptype,
      typelabel: nodeData.typelabel,
      tpParentCompanyName: nodeData.tpParentCompanyName,
      tpAdress: nodeData.tpAdress,
      city: nodeData.city,
      tpFiscalId: nodeData.tpFiscalId,
      tpBrand: nodeData.tpBrand,
      tpReference: nodeData.tpReference,
      child: nodeData.child || null,
      childs: nodeData.childs || [],
    });
  };

  if (!Newdata) {
    return (
      <div className="relationtree no-border-radius ut-wizard-form">
        <HeroTitle
          form={form}
          display
          color={color}
          title="relation"
          itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
          className="relation"
        />
        <div style={{ textAlign: 'center', padding: '20px 100px' }}>
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ width: '100%', height: '120vh', margin: 'auto' }}
      className="relationtree no-border-radius ut-wizard-form"
    >
      <HeroTitle
        form={form}
        display
        color={color}
        title="relation"
        itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
        className="relation"
      />
      <FormInput
        col
        xs={2}
        lg={2}
        name="flagrauto"
        className="form-item no-border-radius large-ant-radio-btn ml-4 mt-4"
        nolabel
      >
        <Radio.Group
          defaultValue={false}
          optionType="button"
          buttonStyle="solid"
          className="radiogrouptree"
          onChange={(value: any) => {
            setorientationH(value.target.value);
          }}
        >
          <Radio value={false}>
            <ApartmentOutlined />
          </Radio>
          <Radio value>
            <ShareAltOutlined />
          </Radio>
        </Radio.Group>
      </FormInput>
      {!orientationH ? (
        treeData.length > 0 ? (
          <RecursiveTree treeData={treeData[0]} onNodeClick={handleNodeClick} />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Aucune donnée disponible
          </div>
        )
      ) : (
        <>
          <Tree
            data={convertToTreeDataHorizontal(Newdata)}
            orientation={'horizontal'}
            translate={{ x: 600, y: 270 }}
            nodeSize={{ x: 250, y: 150 }}
            pathFunc="straight"
            separation={{ siblings: 1, nonSiblings: 1 }}
            renderCustomNodeElement={(props) => (
              <CustomNode
                {...props}
                orientation="horizontal"
                onNodeClick={handleNodeClick}
              />
            )}
            svgClassName={treeData.length > 0 ? treeData[0].tptype : ''}
            draggable={false}
            zoomable
            key={treeData.length > 0 ? treeData[0].tptype : 'tree'}
          />
          <NodeDetailsCard node={selectedNode} />
        </>
      )}
    </div>
  );
};

export default Flow;
